import { AfterViewInit, Component, ElementRef, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Cluster, Contract, Hsts, Site } from '@shared/models/site';
import { HostModeService, SiteMode } from '@shared/services/hostMode.service';
import { isValidDomainName } from '@shared/validators/validators';
import { KeyValuePipe, Location, NgClass, NgFor, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ACL_PUSH_LOG_ADD_SITE, ACL_SITE_ACTIONS, AuthService } from '@shared/services/auth.service';
import { ToastrService } from '@shared/services/toastr.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SitesService } from '@shared/services/sites/sites.service';
import { forkJoin, fromEvent, Observable, of } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { EditMode } from '@shared/edit.mode';
import { ClusterRole, OrganizationCluster } from '@shared/services/organization-cluster.service';
import { BACKEND_PREFIX, STATIC_LINK_OGO_SHIELD_CA_CERT } from 'app/app-routing.constants';
import { DiagResult, ResolveResponse, SiteService, UrlScheme, UrlSchemes } from '@shared/services/site.service';
import { SpringPage } from '@shared/page';
import { OrganizationContractService } from '@shared/services/contract.service';
import {
  CreateSiteRequest,
  OrganizationSiteService,
  UpdateSiteRequest,
} from '@shared/services/organization-site.service';
import { isValid } from 'ipaddr.js';
import { UnsavedChangesService } from '@shared/services/unsaved-state/unsaved-changes.service';
import { DirtyStateService } from '@shared/services/dirty-state/dirty-state.service';
import {
  CustomPagesProfileModel,
  OrganizationCustomPagesProfileService,
} from '@shared/services/custom-page-profiles/organization-custom-pages-profile.service';
import { OrganizationSiteShareService } from '@shared/services/organization-site-share.service';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { LabelComponent } from '@shared/components/label/label.component';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { SiteTagsComponent } from '@shared/components/tags/site-tags.component';
import { TranslateLabelPipe } from '@shared/pipe/filter.pipe';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { FeatureFlagDirective } from '@shared/utils/feature-flag.directive';

@Component({
  selector: 'app-general-config',
  templateUrl: './general-config.component.html',
  styleUrls: ['./general-config.component.scss'],
  imports: [
    SpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    LabelComponent,
    NgClass,
    NgIf,
    NgSelectComponent,
    NgOptionComponent,
    NgStyle,
    NgTemplateOutlet,
    NgFor,
    SwitchComponent,
    SiteTagsComponent,
    KeyValuePipe,
    TranslatePipe,
    TranslateLabelPipe,
    FeatureFlagDirective,
  ],
})
export class GeneralConfigComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) site: Partial<Site> = {};
  @Input({ required: true }) editMode: EditMode;
  @Input({ required: true }) goBack: Function;
  @Input({ required: true }) hasPushLog: boolean;
  @ViewChild('diagnoseModalTpl') diagnoseModalTpl: TemplateRef<any>;
  @ViewChild('domainName') domainName: ElementRef;
  @ViewChild('hostMode') siteMode: ElementRef;
  clusterIps: string[] = [];
  clusters: { cluster: Omit<Cluster, 'owner'>; role: ClusterRole }[] = [];
  contracts: SpringPage<Contract>;
  cluster: Cluster;
  resolveResponse: ResolveResponse;
  provisioningInProgress: boolean = false;
  tags = [];
  primaryDiagnoseResult: DiagResult;
  secondaryDiagnoseResult: DiagResult;
  canSetMode: boolean = false;
  canSetPushLog: boolean = false;
  entryPoints: ClusterEntryPoints;
  secondaryEntryPoints: ClusterEntryPoints;
  form: FormGroup<SiteForm>;
  displayDiagnose: boolean = false;
  onGoingValidation: boolean = false;
  onGoingVerification: boolean = false;
  displayConfirmationMessage: boolean = false;
  secondaryClusterChanged: boolean = false;
  customPagesProfileModels: CustomPagesProfileModel[] = [];
  swapApplied: boolean = false;
  applyingSwap: boolean = false;
  protected readonly UrlSchemes = [...UrlSchemes, 'httpsWithoutHttp2'];
  protected readonly BACKEND_LINK = BACKEND_PREFIX;
  protected canCustomErrorProfile: boolean = false;
  private initialPrimaryUid: string | null = null;
  private initialSecondaryUid: string | null = null;

  constructor(
    protected location: Location,
    private siteService: SiteService,
    private organizationSiteService: OrganizationSiteService,
    public auth: AuthService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private sites: SitesService,
    public hostModeService: HostModeService,
    private contractService: OrganizationContractService,
    private featureFlag: FeatureFlagService,
    private unsavedChanges: UnsavedChangesService,
    protected dirtyState: DirtyStateService,
    protected organizationSiteShareService: OrganizationSiteShareService,
    private organizationCustomPagesProfileService: OrganizationCustomPagesProfileService,
  ) {
    this.clusters = this.auth.currentOrganization.clusters ?? [];
  }

  get clustersSwapped(): boolean {
    return (
      this.cluster?.uid !== this.initialPrimaryUid ||
      (this.form?.controls.secondaryCluster.value ?? null) !== this.initialSecondaryUid
    );
  }

  get secondaryClusterName(): string {
    const uid = this.form?.controls.secondaryCluster.value;
    return this.clusters.find((c) => c.cluster.uid == uid)?.cluster.name ?? '';
  }

  async ngOnInit() {
    this.getTags();
    this.loadCustomPagesProfiles();

    if (!this.site.domainName) {
      this.editMode = 'create';
      this.site = {
        originScheme: 'https',
        auditMode: false,
        forceHttps: true,
        hsts: 'none',
        removeXForwarded: false,
        originSkipCertVerify: true,
        passthroughMode: false,
        logExportEnabled: this.canSetPushLog ? false : undefined,
      };
      this.cluster = this.clusters[0]?.cluster;
      this.buildForm();
      this.loadContracts();
      this.clusterChange();
      this.showSite();
    } else {
      this.editMode = 'update';
      this.cluster = this.site.cluster;
      const organizationCluster = this.clusters.find((c) => c.cluster.uid == this.site.cluster.uid);
      this.canSetMode = organizationCluster && ['ADMIN', 'OWNER'].includes(organizationCluster?.role);
      if (this.site.secondaryCluster && !this.clusters.find((c) => c.cluster.uid == this.site.secondaryCluster.uid)) {
        this.clusters = [...this.clusters, { role: 'USE', cluster: { ...this.site.secondaryCluster } }];
      }
      this.buildForm();
      this.loadContracts();
      this.showSite();
    }
  }
  protected get showCustomErrorProfile(): boolean {
    return this.canCustomErrorProfile && this.featureFlag.featureEnabled('customPagesProfile');
  }
  loadCustomPagesProfiles() {
    const profiles$ = this.site.domainName
      ? this.organizationSiteShareService.getAvailableCustomPagesProfile(this.site.domainName, { number: 0, size: 100 })
      : this.organizationCustomPagesProfileService.getCustomPagesProfiles({ number: 0, size: 100 });

    profiles$.subscribe((page) => {
      this.customPagesProfileModels = page.content;
      this.canCustomErrorProfile = page.content.length > 0;
    });
  }

  clusterChange(applyDefaultMode = true) {
    this.canSetMode = this.isAdminCluster();
    if (applyDefaultMode) {
      const mode = this.canSetMode ? this.cluster?.defaultSiteMode : null;
      this.form.controls.mode.setValue(mode ?? null, { emitEvent: false });
    }
    this.updateClusterEntryPoints();
  }

  ngAfterViewInit() {
    if (this.editMode == 'create') {
      fromEvent(this.domainName.nativeElement, 'keyup')
        .pipe(debounceTime(1000))
        .subscribe((str: any) => {
          this.resolveResponse = {};
          this.checkDnsHandler(str.target.value);
        });
    }
  }

  updateSecondaryClusterEntryPoints() {
    const secondaryClusterUid = this.form.controls.secondaryCluster.value;
    const secondaryCluster = this.clusters.find((c) => c.cluster.uid == secondaryClusterUid)?.cluster;
    this.getClusterEntryPoints(secondaryCluster).subscribe((entryPoints) => (this.secondaryEntryPoints = entryPoints));
  }

  updateClusterEntryPoints() {
    this.getClusterEntryPoints(this.cluster).subscribe((entryPoints) => (this.entryPoints = entryPoints));
  }

  secondaryClusterChange() {
    this.updateSecondaryClusterEntryPoints();
  }

  canSwapClusters(): boolean {
    return (
      this.editMode === 'update' &&
      !!this.form?.controls.secondaryCluster.value &&
      (!this.clustersSwapped || this.swapApplied) &&
      !this.onGoingValidation &&
      !this.provisioningInProgress
    );
  }

  get hasDiagIssues(): boolean {
    return !!this.primaryDiagnoseResult?.issueDetected || !!this.secondaryDiagnoseResult?.issueDetected;
  }

  async showSite() {
    this.updateClusterEntryPoints();
    if (this.site.secondaryCluster) this.updateSecondaryClusterEntryPoints();
    this.resolveResponse = {};

    let https: HttpsEnum;
    if (!this.site.forceHttps) {
      https = 'http';
    } else {
      https = this.site.hsts == 'none' ? 'https' : (this.site.hsts as HttpsEnum);
    }
    this.form.controls.https.setValue(https, { emitEvent: false });

    const originScheme = this.site.originDisableHttp2 ? 'httpsWithoutHttp2' : this.site.originScheme;
    this.form.controls.originScheme.setValue(originScheme, { emitEvent: false });

    this.canSetPushLog =
      this.hasPushLog &&
      (this.editMode === 'update'
        ? this.siteHasAccessRight(ACL_SITE_ACTIONS.PUSH_LOG_SITE)
        : this.auth.userHasFeature(ACL_PUSH_LOG_ADD_SITE));
    this.checkDns(this.form.controls.domainName.value);
  }

  getClusterEntryPoints(cluster: Cluster): Observable<ClusterEntryPoints> {
    if (!cluster) {
      return of(null);
    } else {
      const resolveRequests: Partial<Record<IpType, Observable<ResolveResponse>>> = {};
      if (cluster.ip && !isValid(cluster.ip)) resolveRequests.ipv4 = this.siteService.resolveDns(cluster.ip);
      if (cluster.ipv6 && !isValid(cluster.ipv6)) resolveRequests.ipv6 = this.siteService.resolveDns(cluster.ipv6);
      let entryPoints: ClusterEntryPoints = { ipv4: cluster.ip, ipv6: cluster.ipv6 };
      if (resolveRequests.ipv4 || resolveRequests.ipv6) {
        return forkJoin(resolveRequests).pipe(
          map(({ ipv4, ipv6 }) => {
            this.clusterIps = [];
            if (!!ipv4) {
              this.clusterIps.concat(ipv4.ipv4);
            }
            if (!!ipv6) {
              this.clusterIps.concat(ipv6.ipv6);
            }
            entryPoints = {};
            if (cluster.ip)
              entryPoints.ipv4 =
                cluster.ip + (ipv4?.ipv4 && ipv4.ipv4.length > 0 ? ' (' + ipv4?.ipv4.join(', ') + ')' : ' (DNS ERROR)');
            if (cluster.ipv6)
              entryPoints.ipv6 =
                cluster.ipv6 +
                (ipv6?.ipv6 && ipv6.ipv6.length > 0 ? ' (' + ipv6?.ipv6.join(', ') + ')' : ' (DNS ERROR)');
            return entryPoints;
          }),
        );
      } else {
        return of(entryPoints);
      }
    }
  }

  checkDns(hostName: string) {
    if (hostName == null) {
      return;
    }
    this.resolveResponse = null;
    this.siteService.resolveDns(hostName.trim()).subscribe((res) => {
      this.resolveResponse = res;
      if (!Object.keys(this.resolveResponse).length) {
        return this.toastr.warning(this.translate.instant('NoRecord'));
      }
      if (this.editMode === 'create' && !this.form.controls.originServer.value) {
        if (
          this.resolveResponse.cname !== this.cluster?.cdnCname &&
          !this.resolveResponse.ipv4.some((x) => this.clusterIps.includes(x)) &&
          !this.resolveResponse.ipv6.some((x) => this.clusterIps.includes(x))
        ) {
          if (this.resolveResponse.cname) {
            this.form.controls.originServer.setValue(this.resolveResponse.cname);
          } else if (this.resolveResponse.ipv4?.[0]) {
            this.form.controls.originServer.setValue(this.resolveResponse.ipv4[0]);
          } else if (this.cluster?.supportsIpv6Origins && this.resolveResponse.ipv6?.[0]) {
            this.form.controls.originServer.setValue(this.resolveResponse.ipv6[0]);
          }
        }
      }
    });
  }

  async updateSite() {
    if (!(await this.confirmIfDirty())) {
      return;
    }
    this.provisioningInProgress = true;

    this.organizationSiteService.patchSite(this.form.controls.domainName.value, this.getUpdateSiteRequest()).subscribe({
      next: (_: Site) => {
        this.toastr.success('SiteModifieSucc');
        this.sites.purgeContext();
        this.restoreDefault();
        this.goBack();
      },
      error: () => this.restoreDefault(),
    });
  }

  async createSite() {
    if (!(await this.confirmIfDirty())) {
      return;
    }
    if (this.hasContractValidationError()) {
      this.toastr.error('Site.add.mustChooseContract');
      return;
    }
    this.provisioningInProgress = true;

    this.organizationSiteService.createSite(this.getCreateSiteRequest()).subscribe({
      next: (_: Site) => {
        this.toastr.success('SiteSuccessfullyAdded');
        this.sites.purgeContext();
        this.restoreDefault();
        this.goBack();
      },
      error: () => this.restoreDefault(),
    });
  }

  canValidate() {
    return !(this.provisioningInProgress || this.onGoingValidation || this.hasValidationError());
  }

  hasValidationError() {
    return !this.form.controls.originServer.value || !this.form.controls.domainName.value || !this.cluster;
  }

  hasContractValidationError() {
    return this.contracts && this.contracts.totalElements > 1 && !this.form.controls.contract.value;
  }

  private getUpdateSiteRequest(): UpdateSiteRequest {
    return {
      ...this.getCommonSiteAttributes(),
    };
  }

  protected notPrimaryCluster = (c: OrganizationCluster) => {
    return this.cluster?.uid != c.cluster.uid;
  };

  onPasteDomain(event) {
    if (event.target.value.length == 0 || window.getSelection().toString() == event.target.value) {
      event.preventDefault();
      let paste = (event.clipboardData || window['clipboardData']).getData('text');
      paste = paste.trim();
      this.form.controls.domainName.setValue(paste);
    }
  }

  restoreDefault() {
    this.provisioningInProgress = false;
  }

  verifyOrigin() {
    this.displayDiagnose = true;
    this.onGoingVerification = true;
    this.diagnose(true);
  }

  onPasteDestination(event) {
    if (event.target.value.length == 0 || window.getSelection().toString() == event.target.value) {
      event.preventDefault();
      let paste = (event.clipboardData || window['clipboardData']).getData('text');
      paste = paste.trim();
      if (paste.slice('-1') == '.') {
        paste = paste.slice(0, -1);
      }
      this.form.controls.originServer.setValue(paste);
    }
  }

  async swapClusters() {
    const currentPrimary = this.cluster;
    const targetUid = this.form.controls.secondaryCluster.value;
    const target = this.clusters.find((c) => c.cluster.uid == targetUid)?.cluster;
    if (!currentPrimary || !target) {
      return;
    }
    this.applyingSwap = true;
    this.form.controls.cluster.setValue(target as Cluster);
    this.form.controls.secondaryCluster.setValue(currentPrimary.uid);
    this.applyingSwap = false;

    if (this.clustersSwapped) {
      this.swapApplied = true;
      this.form.controls.secondaryCluster.disable({ emitEvent: false });
      this.form.markAsDirty();
    } else {
      this.swapApplied = false;
      this.form.controls.secondaryCluster.enable({ emitEvent: false });
      this.form.markAsPristine();
    }
    this.secondaryClusterChanged = this.clustersSwapped;
  }
  get secondaryCluster(): Cluster | undefined {
    const uid = this.form?.controls.secondaryCluster.value;
    return this.clusters.find((c) => c.cluster.uid === uid)?.cluster;
  }
  get outgoingIpsView() {
    const primary = this.cluster;
    const secondary = this.secondaryCluster;
    const clusters = [primary, secondary].filter((c): c is Cluster => !!c);

    const entries: { label: string; ips: string }[] = [];
    if (!!primary && !primary.isPublic) {
      entries.push({
        label: secondary ? 'PrimaryIpSortie' : 'IPsToWhitelist',
        ips: joinIps(primary!.trustedIps),
      });
    }
    if (!!secondary && !secondary.isPublic) {
      entries.push({ label: 'SecondaryIpSortie', ips: joinIps(secondary!.trustedIps) });
    }

    return { entries, showPublicLink: clusters.some((c) => c.isPublic) };
  }
  diagnose(clearAfter = false): Promise<void> {
    this.primaryDiagnoseResult = null;
    this.secondaryDiagnoseResult = null;
    this.onGoingValidation = true;

    const hasSecondary = !!this.form.get('secondaryCluster').value;

    const requests: Observable<DiagResult>[] = [this.siteService.diagnose(this.getDiagRequest(false))];
    if (hasSecondary) {
      requests.push(this.siteService.diagnose(this.getDiagRequest(true)));
    }

    return new Promise((resolve) => {
      forkJoin(requests).subscribe({
        next: ([primary, secondary]) => {
          this.primaryDiagnoseResult = primary;
          this.secondaryDiagnoseResult = secondary ?? null;
          if (primary?.issueDetected || secondary?.issueDetected) {
            this.dirtyState.setDirty();
          }
          if (clearAfter) {
            this.dirtyState.clear();
          }
          this.onGoingValidation = false;
          this.onGoingVerification = false;
          resolve();
        },
        error: () => {
          this.toastr.error('OriginInvalid');
          this.onGoingValidation = false;
          this.onGoingVerification = false;
          resolve();
        },
      });
    });
  }

  checkDnsHandler(domain = ''): boolean {
    if (domain === undefined) {
      return;
    }

    if (!isValidDomainName(domain)) {
      this.toastr.info('AddressNotValid');
      return;
    }

    this.checkDns(domain);
  }

  private getCreateSiteRequest(): CreateSiteRequest {
    return {
      ...this.getCommonSiteAttributes(),
      contract: this.form.controls.contract.value,

      logExportEnabled: this.canSetPushLog ? this.form.controls.logExportEnabled.value : undefined,
    };
  }

  private getCommonSiteAttributes() {
    const https = this.form.controls.https.value;
    return {
      ...this.getDiagRequest(),
      mode: this.form.controls.mode.value,
      auditMode: this.form.controls.auditMode.value,
      passthroughMode: this.form.controls.passthroughMode.value,
      removeXForwarded: this.form.controls.removeXForwarded.value,
      logExportEnabled: this.form.controls.logExportEnabled.value,
      tags: this.form.controls.tags.value,
      secondaryCluster: this.form.controls.secondaryCluster.value,
      customPagesProfile: this.form.controls.customPagesProfile.value,
      forceHttps: https != 'http',
      hsts: (https == 'http' || https == 'https' ? 'none' : https) as Hsts,
    };
  }

  clearDiagnoseResult() {
    this.primaryDiagnoseResult = null;
    this.secondaryDiagnoseResult = null;
  }

  private buildForm() {
    this.form = new FormGroup<SiteForm>({
      domainName: new FormControl({ value: this.site.domainName ?? null, disabled: this.editMode == 'update' }),
      contract: new FormControl(this.site.contract ?? null),
      cluster: new FormControl(this.cluster ?? null),
      secondaryCluster: new FormControl(this.site.secondaryCluster?.uid ?? null),
      originServer: new FormControl(this.site.originServer ?? null),
      originPort: new FormControl(this.site.originPort ?? null),
      https: new FormControl('https'),
      originScheme: new FormControl('https'),
      auditMode: new FormControl(this.site.auditMode ?? false),
      tags: new FormControl(this.site.tags ?? []),
      removeXForwarded: new FormControl(this.site.removeXForwarded ?? false),
      originSkipCertVerify: new FormControl(this.site.originSkipCertVerify ?? true),
      originMtlsEnabled: new FormControl(this.site.originMtlsEnabled ?? false),
      passthroughMode: new FormControl(this.site.passthroughMode ?? false),
      mode: new FormControl<SiteMode | null>(this.site.mode ?? null),
      logExportEnabled: new FormControl(this.site.logExportEnabled ?? false),
      customPagesProfile: new FormControl(this.site.customPagesProfile?.uid ?? null),
    });

    this.initialPrimaryUid = this.site.cluster?.uid ?? this.cluster?.uid ?? null;
    this.initialSecondaryUid = this.site.secondaryCluster?.uid ?? null;

    this.form.controls.cluster.valueChanges.subscribe((cluster) => {
      this.cluster = cluster;
      this.clusterChange(this.editMode === 'create');
      this.clearDiagnoseResult();
    });
    this.form.controls.secondaryCluster.valueChanges.subscribe(() => {
      this.secondaryClusterChange();
      if (!this.applyingSwap) {
        this.swapApplied = false;
        this.secondaryClusterChanged = this.clustersSwapped;
      }
      this.clearDiagnoseResult();
    });
    this.form.controls.originScheme.valueChanges.subscribe(() => this.clearDiagnoseResult());
    this.form.controls.originSkipCertVerify.valueChanges.subscribe(() => this.clearDiagnoseResult());
    this.form.controls.originMtlsEnabled.valueChanges.subscribe(() => this.clearDiagnoseResult());
    this.form.controls.originPort.valueChanges.subscribe(() => this.clearDiagnoseResult());
    this.form.controls.originServer.valueChanges.subscribe(() => this.clearDiagnoseResult());
    this.form.controls.domainName.valueChanges.subscribe(() => this.clearDiagnoseResult());
  }

  private isAdminCluster() {
    if (!this.cluster) return false;
    return ['ADMIN', 'OWNER'].includes(
      this.clusters.find((organizationCluster) => organizationCluster.cluster.uid == this.cluster.uid)?.role,
    );
  }

  async getTags() {
    this.tags = await this.sites.loadTags();
  }

  siteHasAccessRight(right) {
    return this.site?.accessRights?.includes(right);
  }

  private async confirmIfDirty(): Promise<boolean> {
    this.displayConfirmationMessage = true;

    const needsSecondary = !!this.form.get('secondaryCluster').value;
    if (!this.primaryDiagnoseResult || (needsSecondary && !this.secondaryDiagnoseResult)) {
      await this.diagnose();
    }

    if (!this.dirtyState.isDirty()) {
      return true;
    }
    const confirmed = await this.unsavedChanges.confirm({
      titleKey: 'ProblemVerif',
      messageTemplate: this.diagnoseModalTpl,
      cancelKey: 'Annuler',
      confirmKey: 'Confirmer',
      windowClass: 'large-modal',
    });
    if (!confirmed) {
      this.displayConfirmationMessage = false;
      this.displayDiagnose = true;
      return false;
    }
    this.displayConfirmationMessage = false;
    this.dirtyState.clear();
    return true;
  }

  protected readonly STATIC_LINK_OGO_SHIELD_CA_CERT = STATIC_LINK_OGO_SHIELD_CA_CERT;
  protected readonly Object = Object;

  private loadContracts() {
    this.contractService.getAvailableContracts({ number: 0, size: 10 }).subscribe((page) => {
      this.contracts = page;
      if (page.totalElements == 1 && !this.form.controls.contract.value) {
        this.form.controls.contract.setValue(page.content[0]);
      }
    });
  }

  private getDiagRequest(secondary = false) {
    const originScheme = this.form.controls.originScheme.value;
    return {
      originPort: this.form.controls.originPort.value,
      originMtlsEnabled: this.form.controls.originMtlsEnabled.value,
      originSkipCertVerify: this.form.controls.originSkipCertVerify.value,
      originScheme: originScheme == 'httpsWithoutHttp2' ? 'https' : originScheme,
      originDisableHttp2: originScheme == 'httpsWithoutHttp2',
      cluster: secondary ? this.form.get('secondaryCluster').value : this.cluster?.uid,
      domainName: this.form.controls.domainName.value?.trim().toLowerCase() ?? '',
      originServer: this.form.controls.originServer.value?.trim() ?? '',
    };
  }
}

type HttpsEnum = 'http' | 'https' | 'hsts' | 'hstss' | 'hstssp';

type IpType = 'ipv4' | 'ipv6';

type ClusterEntryPoints = Partial<Record<IpType, string>>;

interface SiteForm {
  domainName: FormControl<string | null>;
  contract: FormControl<Contract | null>;
  cluster: FormControl<Cluster | null>;
  secondaryCluster: FormControl<string | null>;
  originServer: FormControl<string | null>;
  originPort: FormControl<number | null>;
  https: FormControl<HttpsEnum | null>;
  originScheme: FormControl<UrlScheme | 'httpsWithoutHttp2' | null>;
  auditMode: FormControl<boolean | null>;
  tags: FormControl<any[] | null>;
  removeXForwarded: FormControl<boolean | null>;
  originSkipCertVerify: FormControl<boolean | null>;
  originMtlsEnabled: FormControl<boolean | null>;
  passthroughMode: FormControl<boolean | null>;
  mode: FormControl<SiteMode | null>;
  logExportEnabled: FormControl<boolean | null>;
  customPagesProfile: FormControl<string | null>;
}

function joinIps(ips: string[]): string {
  return ips.length > 0 ? ips.join(', ') : '—';
}
